'use client';

import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { CheckIcon, StarIcon } from '@heroicons/react/20/solid';
import { formatPrice } from '../../services/formatPrice';
import getSubscriptionIntervalWithSlash from '../../services/translation/getSubscriptionIntervalWithSlash';
import { groupLabelFor, derivedGroups, resolveAutoGroupBy } from '../../services/priceGrouping';
import useMountEffect from '../../hooks/useMountEffect';
import PayWhatYouWantPriceField from './PayWhatYouWantPriceField';
import PriceDescription from './PriceDescription';
import Proration from '../Product/Proration';
import StyledElement from '../StyledElement';
import BadgeIcon, { isBadgeIcon } from '../BadgeIcon';
import { isMultiPrice, selectablePrices } from '../../lib/multiPrice';
import isPaymentPlanPrice from '../../lib/isPaymentPlanPrice';
import getPriceBillingConfig from '../../lib/getPriceBillingConfig';
import { checkHasBillingCycleAnchorConfig } from '../../lib/checkHasBillingCycleAnchorConfig';
import { queryDiscountedFrom, queryPriceField } from '../../lib/discountedFrom';
import checkHasValidStartDate from '../../lib/checkHasValidStartDate';
import formatStartDate from '../../lib/formatStartDate';
import Label from '../CheckoutForm/components/Label';

/**
 * Format the fixed price amount for a PricePicker option, e.g. "$49" or "$10".
 * The recurring interval is rendered separately (see formatPriceOptionInterval)
 * so the shared Interval theme element can style it independently.
 */
function formatPriceOptionAmount(price, currencyLocale) {
  if (!price) return '';
  const currency = price.currency || 'usd';
  const amount = price.amount ?? 0;
  return formatPrice({ amount, currency, locale: currencyLocale, style: 'currency' });
}

/**
 * The subscription interval suffix for a recurring option, e.g. "/ month".
 * Empty string for one-time prices.
 */
function formatPriceOptionInterval(price, labels = {}) {
  if (!price) return '';
  const config = getPriceBillingConfig(price);
  if (price.billingType === 'recurring' && config?.interval) {
    return getSubscriptionIntervalWithSlash(config.interval, config.intervalCount, labels);
  }
  return '';
}

/**
 * PricePicker — customer-facing price selector rendered on the storefront.
 *
 * Rendered ONLY when product.prices has 2+ enabled prices. Single-price
 * products skip this entirely.
 *
 * When `groupBy` is `billing` or `interval` and there are 2+ distinct groups,
 * prices are presented as group tabs derived automatically from each price's
 * billingType / interval. Otherwise falls back to a flat radio list so no
 * price is ever hidden.
 *
 * Props:
 *   prices                – array of price subdocs from the product
 *   defaultPriceId        – string | null — pre-selected price id (flat list mode)
 *   selectedPriceId       – controlled selected price id
 *   onSelect              – (priceId: string) => void
 *   labels                – theme labels object
 *   groupBy               – 'auto' | 'billing' | 'interval' | 'none' — grouping axis.
 *                           'auto' derives the axis from the current price mix at
 *                           render time. It is the theme default (see mergeTheme),
 *                           so an absent value here is an explicit non-grouping one.
 *   priceDisplay          – 'list' | 'grid' — layout of the option list (default 'list')
 *   gridColumns           – number — columns when priceDisplay='grid' (default 2)
 *   horizontalAlign       – 'left' | 'center' | 'right' — option content horizontal alignment (default 'left')
 *   verticalAlign         – 'top' | 'center' | 'bottom' — option content vertical alignment (default 'top')
 *   groupingMode          – 'horizontal' | 'vertical' — tab bar orientation (default 'horizontal')
 *   priceDisplayLabels    – { [axis]: { [derivedGroup]: overrideLabel } } — override tab button text per group
 *   priceDisplayDefaults  – { [axis]: defaultGroup } — which derived group tab is active by default
 *   priceDisplayTabOrder  – { [axis]: [group, ...] } — explicit ordering of tabs
 *   className             – optional extra className
 */
// Text-only badges keep their inline-block; only one that leads with an icon
// becomes a row, so nothing already themed shifts (see checkout.css).
function PriceBadge({ price, className }) {
  const hasIcon = isBadgeIcon(price.badgeIcon);
  if (!price.badge && !hasIcon) return null;

  return (
    <StyledElement
      as="span"
      elementPath="product.pricePicker.option.badge"
      className={clsx(className, { 'cp_price-picker-option-badge--with-icon': hasIcon })}
    >
      {hasIcon && <BadgeIcon icon={price.badgeIcon} />}
      {price.badge}
    </StyledElement>
  );
}

const PricePicker = ({
  prices = [],
  query = {},
  allowDynamicPrice = false,
  allowDynamicDiscountedFromPrice = false,
  allowDynamicLabel = false,
  allowDynamicDescription = false,
  allowDynamicPlanIterations = false,
  defaultPriceId,
  selectedPriceId,
  onSelect,
  onPwywOptionActivate,
  labels = {},
  currencyLocale,
  groupBy,
  priceDisplay = 'list',
  gridColumns = 2,
  horizontalAlign = 'left',
  verticalAlign = 'top',
  pwywDisplay = 'inline',
  indicatorShape = 'circle',
  shortenDescription = true,
  groupingMode = 'horizontal',
  priceDisplayLabels = {},
  priceDisplayDefaults = {},
  priceDisplayTabOrder = {},
  className,
}) => {
  // Selectable (visible) options exclude disabled + hidden prices (hidden are
  // revealable via the query). Hidden prices still COUNT toward "multi-price", so a
  // product with a hidden sibling shows the picker with its visible option(s).
  const enabledPrices = selectablePrices(prices, query);
  const showPicker = isMultiPrice(prices, query);

  const resolvedGroupBy = groupBy === 'auto' ? resolveAutoGroupBy(enabledPrices) : groupBy;
  const axis =
    resolvedGroupBy === 'billing' || resolvedGroupBy === 'interval' ? resolvedGroupBy : null;
  const derived = derivedGroups(enabledPrices, axis);
  const useTabs = !!axis && derived.length >= 2;

  // Apply saved tab order: take entries from the saved order that exist in derived,
  // then append any derived groups not covered by the saved order.
  const savedOrder = (axis && priceDisplayTabOrder[axis]) || [];
  const orderedGroups = useTabs
    ? [
        ...savedOrder.filter(g => derived.includes(g)),
        ...derived.filter(g => !savedOrder.includes(g)),
      ]
    : derived;

  // Resolve the initial/default active group.
  const savedDefault = axis && priceDisplayDefaults[axis];
  const initialGroup = useTabs
    ? savedDefault && orderedGroups.includes(savedDefault)
      ? savedDefault
      : orderedGroups[0]
    : null;

  // Display label for a derived group key (button text only; matching is always by derived key).

  const displayLabel = group => (axis ? priceDisplayLabels[axis]?.[group] : undefined) ?? group;

  // Open directly on an already-selected (or merchant-default) price's tab. A
  // remount — e.g. Stripe Elements re-keys when the billing mode changes on a
  // mixed-billing product — otherwise seeds the first tab here and only corrects
  // to the selected price's tab in the mount effect below, flashing the wrong tab
  // for a frame. Resolving it up front makes the first paint correct.
  const [activeGroup, setActiveGroup] = useState(() => {
    if (!useTabs) return initialGroup;
    const preselect =
      (selectedPriceId && enabledPrices.find(p => String(p._id) === String(selectedPriceId))) ||
      (defaultPriceId && enabledPrices.find(p => String(p._id) === String(defaultPriceId)));
    return preselect ? groupLabelFor(preselect, axis) : initialGroup;
  });

  const labelOf = p => groupLabelFor(p, axis);

  useMountEffect(() => {
    if (!showPicker) return;
    // An already-chosen price → the merchant default → the lone visible option. With
    // no default and 2+ visible options the picker opens with nothing selected, so the
    // customer must make a pricing decision.
    //
    // The `?<reference>_preselected=1` URL param is NOT read here: switching billing
    // type re-keys Stripe Elements and remounts this component, so re-applying the
    // still-present param would snap the customer's new choice back and make the other
    // billing type unreachable. It is resolved once in getInitialSelectedPriceId and
    // arrives as `selectedPriceId`.
    const preselect =
      (selectedPriceId && enabledPrices.find(p => String(p._id) === String(selectedPriceId))) ||
      (defaultPriceId && enabledPrices.find(p => String(p._id) === String(defaultPriceId))) ||
      (enabledPrices.length === 1 ? enabledPrices[0] : null);
    if (!preselect) return;
    // Only seed when nothing is chosen yet — never clobber an existing choice on remount.
    if (onSelect && !selectedPriceId) onSelect(String(preselect._id));
    // In tabs mode, surface the pre-selected price's tab so the selection is visible.
    if (useTabs) setActiveGroup(labelOf(preselect));
  });

  // Re-sync the active tab when the grouping axis changes at runtime (the design
  // editor mutates groupBy without remounting). Mount seeds activeGroup once via
  // useState, so a none -> billing/interval switch would otherwise leave it null
  // and no tab selected; a billing <-> interval switch would leave a stale group.
  useEffect(() => {
    if (!useTabs) return;
    if (!activeGroup || !orderedGroups.includes(activeGroup)) {
      setActiveGroup(initialGroup);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useTabs, axis, orderedGroups.join('|'), initialGroup]);

  // Keep groups alias for internal filtering (derived key matching).
  const groups = orderedGroups;

  // Switching to a tab surfaces that tab WITHOUT auto-picking a price: the
  // customer must choose an option. The one exception is the merchant's
  // preselected (default) price — if it lives in the chosen tab, select it;
  // otherwise clear the selection so nothing is implicitly bought.
  const selectTab = group => {
    setActiveGroup(group);
    if (!onSelect) return;
    const defaultInTab =
      defaultPriceId &&
      enabledPrices.find(p => labelOf(p) === group && String(p._id) === String(defaultPriceId));
    onSelect(defaultInTab ? String(defaultInTab._id) : null);
  };

  // Single price: no picker. The lone price's badge is rendered by Product
  // (the single-price path), since PricePicker isn't mounted for one price.
  if (!showPicker) return null;

  const isGrid = priceDisplay === 'grid';
  const isVerticalTabs = groupingMode === 'vertical';

  // Two independent alignment axes (replacing the old single `--center`):
  // horizontal left|center|right + vertical top|center|bottom. CSS keys off the
  // --h-*/--v-* modifiers and applies them differently for list vs grid.
  const hAlign = ['left', 'center', 'right'].includes(horizontalAlign) ? horizontalAlign : 'left';
  const vAlign = ['top', 'center', 'bottom'].includes(verticalAlign) ? verticalAlign : 'top';
  // Selected-mark shape for the radio indicator (dot | tick | star). The ring's
  // roundness is a separate Border radius control, not part of the shape.
  const shape = ['dot', 'tick', 'star'].includes(indicatorShape) ? indicatorShape : 'dot';
  const pwywMode = ['inline', 'button', 'standalone'].includes(pwywDisplay)
    ? pwywDisplay
    : 'inline';

  const optionListClass = clsx(
    'cp_price-picker',
    `cp_price-picker--h-${hAlign}`,
    `cp_price-picker--v-${vAlign}`,
    {
      'cp_price-picker--grid': isGrid,
    },
  );

  const optionListStyle = isGrid ? { '--cp-price-grid-cols': gridColumns } : undefined;

  // List layout right-aligns the badge in the header row; grid positions it via its
  // own column rules. Decided here rather than with an ancestor :not(--grid)
  // selector, which the tabs-mode outer .cp_price-picker wrapper falsely matches.
  // max-w-full/wrapping keep a long highlight inside the option card. #1576 gave the
  // left-aligned list its own break-onto-a-new-line rule; grid, h-center and h-right
  // have no such row to escape, so they need the badge itself to wrap.
  const badgeClass = clsx(
    'cp_price-picker-option-badge',
    'max-w-full whitespace-normal break-words',
    !isGrid && 'ml-auto',
  );

  const computeOptionContext = price => {
    const priceId = String(price._id);
    const isSelected = String(selectedPriceId) === priceId;
    // Fixed-price option label, e.g. "$49" or "$10 / month". Pay-what-you-want
    // options skip this price line entirely (see below): their "Pay what you want"
    // text comes from the amount input's own label, so rendering it here too would
    // duplicate it.
    // Per-price amount override (`${reference}_price`), gated by allowDynamicPrice,
    // mirroring the cart/charge override in useCheckoutState so the option price,
    // subtotal and total all reflect the same dynamic amount. The picker only renders
    // for multi-price products, so the legacy unprefixed params never apply here —
    // they carry no reference and would hit every option at once.
    const dynamicAmount = allowDynamicPrice
      ? Number(queryPriceField(price.reference, 'price', query, { allowLegacyFallback: false }))
      : NaN;
    const effectivePrice =
      Number.isFinite(dynamicAmount) && dynamicAmount >= 0
        ? { ...price, amount: dynamicAmount }
        : price;
    const optionAmount = formatPriceOptionAmount(effectivePrice, currencyLocale);
    const optionInterval = formatPriceOptionInterval(effectivePrice, labels);
    // Per-price label/description URL overrides (`${reference}_label` /
    // `${reference}_description`), each behind its gate; fall back to the stored value.
    const effectiveLabel =
      (allowDynamicLabel &&
        queryPriceField(price.reference, 'label', query, { allowLegacyFallback: false })) ||
      price.label;
    const effectiveDescription =
      (allowDynamicDescription &&
        queryPriceField(price.reference, 'description', query, { allowLegacyFallback: false })) ||
      price.description;
    // The struck-through original price shown next to a discounted fixed price
    // (~~$10~~ $5). Skipped for pay-what-you-want (no fixed amount to discount).
    // Effective discounted-from: the per-price `${reference}_discounted_from` override
    // when the merchant allows it, otherwise the price's stored value.
    const dynamicDiscountedFrom = allowDynamicDiscountedFromPrice
      ? queryDiscountedFrom(price.reference, query, { allowLegacyFallback: false })
      : NaN;
    const effectiveDiscountedFrom =
      dynamicDiscountedFrom >= 0 ? dynamicDiscountedFrom : price.discountedFromPrice;
    const discountedFrom =
      !price.payWhatYouWant && effectiveDiscountedFrom > 0
        ? formatPrice({
            amount: effectiveDiscountedFrom,
            currency: price.currency || 'usd',
            locale: currencyLocale,
            style: 'currency',
          })
        : null;

    // Effective billing config (recurring XOR paymentPlan; flat pre-migration
    // subdocs via the helper's fallback) — the one read site for the option's
    // trial/startDate/planIterations lines.
    const billingConfig = getPriceBillingConfig(price);
    // A recurring price with a trial shows "N days free" + a "then $X / interval"
    // line, matching the single-price checkout (Product.js). Guard on billingType so
    // a stray trialPeriodDays on a one-time price is ignored.
    const isTrial = price.billingType === 'recurring' && billingConfig?.trialPeriodDays > 0;
    // A per-price one-time setup fee, shown as a muted sub-line (amount + label),
    // matching the single-price checkout's SetupFee wording.
    const setupFeeLabel =
      price.setupFee > 0
        ? `${formatPrice({
            amount: price.setupFee,
            locale: currencyLocale,
            currency: price.currency || 'usd',
            style: 'currency',
          })} ${labels.setupFeeText ?? 'setup fee'}`
        : null;
    // A per-price subscription start date ("Starts 22 Jun, 2026"), matching the
    // single-price checkout (Product.js) — multi-price stores startDate on the
    // price, so each recurring option shows its own delayed-start date.
    const startsOnLabel = checkHasValidStartDate(billingConfig?.startDate)
      ? `${labels.startsOnText ?? 'Starts'} ${formatStartDate(billingConfig.startDate)}`
      : null;
    // A payment plan's "Ends after N payments", matching the single-price checkout
    // (Product.js). Only meaningful for a recurring price with a fixed iteration count.
    // Mirrors the cart's `${reference}_no_of_payments` override (useCheckoutState) so
    // the option can't advertise a different plan length than the buyer is charged
    // for — same gates: the merchant flag, and a price that is already a plan.
    const dynamicIterations = allowDynamicPlanIterations
      ? Number(
          queryPriceField(price.reference, 'no_of_payments', query, {
            allowLegacyFallback: false,
          }),
        )
      : NaN;
    const effectiveIterations =
      isPaymentPlanPrice(price) && Number.isFinite(dynamicIterations) && dynamicIterations > 0
        ? dynamicIterations
        : billingConfig?.planIterations;
    const endsAfterLabel =
      price.billingType === 'recurring' && effectiveIterations > 0
        ? `${labels.endsAfterText ?? 'Ends after'} ${effectiveIterations} ${
            labels.endsAfterPaymentsText ?? 'payments'
          }`
        : null;
    const trialLine = isTrial ? (
      <StyledElement
        as="div"
        elementPath="product.pricePicker.option.trialPeriod"
        className="cp_price-picker-option-trial"
      >
        {`${billingConfig?.trialPeriodDays} ${labels.trialDaysFreeText ?? 'days free'}`}
      </StyledElement>
    ) : null;
    return {
      priceId,
      isSelected,
      effectivePrice,
      optionAmount,
      optionInterval,
      effectiveLabel,
      effectiveDescription,
      discountedFrom,
      billingConfig,
      isTrial,
      setupFeeLabel,
      startsOnLabel,
      endsAfterLabel,
      trialLine,
    };
  };

  const renderOptionMeta = (price, ctx) => (
    <>
      {price.payWhatYouWant && ctx.trialLine}
      {ctx.startsOnLabel && (
        <StyledElement
          as="div"
          elementPath="product.pricePicker.option.startDate"
          className="cp_price-picker-option-starts-on"
        >
          {ctx.startsOnLabel}
        </StyledElement>
      )}
      {ctx.endsAfterLabel && (
        <StyledElement
          as="div"
          elementPath="product.pricePicker.option.planIterations"
          className="cp_price-picker-option-ends-after"
        >
          {ctx.endsAfterLabel}
        </StyledElement>
      )}
      {checkHasBillingCycleAnchorConfig(price) && (
        <div className="cp_price-picker-option-proration">
          <Proration price={price} elementPathPrefix="product.pricePicker.option" />
        </div>
      )}
      {ctx.setupFeeLabel && (
        <StyledElement
          as="div"
          elementPath="product.pricePicker.option.setupFee"
          className="cp_price-picker-option-setup-fee"
        >
          {ctx.setupFeeLabel}
        </StyledElement>
      )}
      {ctx.effectiveDescription && (
        <StyledElement
          as="div"
          elementPath="product.pricePicker.option.description"
          className={clsx(
            'cp_price-picker-option-description break-words',
            '[color:var(--cp_price-picker-option-description-color)]',
            '[font-family:var(--cp_price-picker-option-description-font-family)]',
            '[font-style:var(--cp_price-picker-option-description-font-style)]',
            '[font-size:var(--cp_price-picker-option-description-font-size)]',
            '[line-height:var(--cp_price-picker-option-description-line-height)]',
            '[font-weight:var(--cp_price-picker-option-description-font-weight)]',
          )}
        >
          <PriceDescription
            // A new text is a new description: remount so it measures its own
            // overflow from a collapsed start rather than inheriting the last one's.
            key={ctx.effectiveDescription}
            text={ctx.effectiveDescription}
            isShortenable={shortenDescription}
            labels={labels}
          />
        </StyledElement>
      )}
    </>
  );

  const renderOption = price => {
    const ctx = computeOptionContext(price);
    const {
      priceId,
      isSelected,
      effectiveLabel,
      optionAmount,
      optionInterval,
      discountedFrom,
      isTrial,
      trialLine,
    } = ctx;
    return (
      // The whole option card is the clickable control — a `<div role="radio">`
      // rather than a `<button>` so a PWYW option's amount `<input>` can live
      // inside it (an input can't legally nest in a button). Clicking anywhere on
      // the card selects the price; the card is keyboard-focusable and Enter/Space
      // selects it.
      <StyledElement
        as="div"
        key={priceId}
        elementPath="product.pricePicker.option"
        className={clsx(
          'cp_price-picker-option flex flex-col w-full text-left overflow-hidden',
          'border-solid [transition:border-color_0.15s_ease]',
          '[border-width:var(--cp_price-picker-option-border-width)]',
          '[border-radius:var(--cp_price-picker-option-border-radius)]',
          '[color:var(--cp_price-picker-option-color)]',
          {
            'cp_price-picker-option--selected': isSelected,
            'bg-[var(--cp_price-picker-option-background-color)] border-[var(--cp_price-picker-option-border-color)]':
              !isSelected,
            'bg-[var(--cp_price-picker-option-background-color-selected)] border-[var(--cp_price-picker-option-border-color-selected)] [box-shadow:inset_0_0_0_1px_var(--cp_price-picker-option-border-color-selected)]':
              isSelected,
          },
        )}
      >
        <div
          role="radio"
          tabIndex={0}
          aria-checked={isSelected}
          className="cp_price-picker-option-button"
          onClick={e => {
            onSelect && onSelect(priceId);
            if (price.payWhatYouWant) {
              e.currentTarget.querySelector('input')?.focus();
              onPwywOptionActivate && onPwywOptionActivate(priceId);
            }
          }}
          onKeyDown={e => {
            // Activate from the card itself (Enter/Space). Gated to the card so
            // typing in the PWYW input nested inside it isn't hijacked.
            if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
              e.preventDefault();
              onSelect && onSelect(priceId);
              if (price.payWhatYouWant) onPwywOptionActivate && onPwywOptionActivate(priceId);
            }
          }}
        >
          <div className="cp_price-picker-option-content">
            <div className="cp_price-picker-option-lines">
              <div className="cp_price-picker-option-header">
                {effectiveLabel && (
                  <div className="cp_price-picker-option-text">
                    <StyledElement
                      // A PWYW option's heading labels the amount input in the
                      // card, so it stays a real <label> (the Label component,
                      // markdown included); its unlayered pwywLabel rule in
                      // checkout.css overrides Label's Fields styling. A
                      // fixed-price option's label is just a card title — a
                      // plain <div>, so Label's Fields font-weight/padding
                      // can't leak onto it.
                      as={price.payWhatYouWant ? Label : 'div'}
                      elementPath="product.pricePicker.option.label"
                      className={clsx(
                        // `cp_price-picker-option-label` stays on every option (seller
                        // custom-CSS hook + the eye-toggle's arbitrary-value styling
                        // below). A PWYW option additionally gets
                        // `cp_price-picker-option-pwyw-label`, which in checkout.css
                        // consumes the pwywLabel chain (product.pricePicker.option.pwywLabel
                        // -> checkout.pwyw.label -> Fields > Label) and wins the tie so a
                        // PWYW option's heading follows the PWYW label styling instead of
                        // the fixed-price option-label look. Visibility is still governed
                        // solely by the option.label.enabled toggle above (StyledElement
                        // renders null when disabled) — the pwywLabel group intentionally
                        // has no toggle of its own.
                        'cp_price-picker-option-label break-words',
                        price.payWhatYouWant && 'cp_price-picker-option-pwyw-label',
                        '[color:var(--cp_price-picker-option-label-color)]',
                        '[font-family:var(--cp_price-picker-option-label-font-family)]',
                        '[font-style:var(--cp_price-picker-option-label-font-style)]',
                        '[font-size:var(--cp_price-picker-option-label-font-size)]',
                        '[line-height:var(--cp_price-picker-option-label-line-height)]',
                        '[font-weight:var(--cp_price-picker-option-label-font-weight)]',
                      )}
                    >
                      {effectiveLabel}
                    </StyledElement>
                  </div>
                )}
                <PriceBadge price={price} className={badgeClass} />
              </div>
              {!price.payWhatYouWant && trialLine}
              {/* A pay-what-you-want option renders its amount input here, in the
                  price's place (its own "Pay what you want" label leads it), so the
                  input is part of the card content — no separate row below the card,
                  and the whole card stays clickable. The input shows for every PWYW
                  option up-front (not gated on selection). A fixed price renders the
                  usual amount line instead. */}
              {price.payWhatYouWant ? (
                <div className="cp_price-picker-option-pwyw">
                  <PayWhatYouWantPriceField
                    price={price}
                    labels={labels}
                    isSelected={isSelected}
                    showCurrency
                    suffixElementPath="product.pricePicker.option.pwywSuffix"
                    onSelect={() => onSelect && onSelect(priceId)}
                    hideLabel={true}
                  />
                </div>
              ) : price.amount > 0 ? (
                <StyledElement
                  as="div"
                  elementPath="product.pricePicker.option.price"
                  className="cp_price-picker-option-price"
                >
                  {isTrial && (
                    <span className="cp_price-picker-option-trial-then">
                      {labels.trialThenText ?? 'then'}{' '}
                    </span>
                  )}
                  {discountedFrom && (
                    <StyledElement
                      as="span"
                      elementPath="product.pricePicker.option.discountedFromPrice"
                      className="cp_price-picker-option-discounted-from"
                    >
                      {discountedFrom}
                    </StyledElement>
                  )}
                  {optionAmount}
                  {optionInterval && (
                    <StyledElement
                      as="span"
                      elementPath="product.pricePicker.option.interval"
                      className={clsx(
                        'cp_price-picker-option-interval',
                        '[color:var(--cp_price-picker-option-interval-color)]',
                        '[font-family:var(--cp_price-picker-option-interval-font-family)]',
                        '[font-style:var(--cp_price-picker-option-interval-font-style)]',
                        '[font-size:var(--cp_price-picker-option-interval-font-size)]',
                        '[line-height:var(--cp_price-picker-option-interval-line-height)]',
                        '[font-weight:var(--cp_price-picker-option-interval-font-weight)]',
                      )}
                    >
                      {' '}
                      {optionInterval}
                    </StyledElement>
                  )}
                </StyledElement>
              ) : (
                <></>
              )}

              {renderOptionMeta(price, ctx)}
            </div>
          </div>
          {/* The selection circle. Wrapped in StyledElement so the "Show radio"
              design toggle can hide it (renders null when disabled). Decorative
              only — the role="radio"/aria-checked live on the button above, so
              hiding the circle is purely cosmetic; selection is still conveyed by
              the selected card's border/background. */}
          <StyledElement
            as="span"
            elementPath="product.pricePicker.option.indicator"
            className={clsx(
              'cp_price-picker-option-indicator',
              `cp_price-picker-option-indicator--shape-${shape}`,
              {
                'cp_price-picker-option-indicator--checked': isSelected,
              },
            )}
            aria-hidden="true"
          >
            {/* Tick/star are SVG icons rather than ✓/★ glyphs so the mark doesn't
                change weight or centring with the themed font. Dot stays pure CSS
                (the --checked::after box) — it's geometry, not a glyph. */}
            {isSelected && shape === 'tick' && (
              <CheckIcon className="cp_price-picker-option-indicator-icon" />
            )}
            {isSelected && shape === 'star' && (
              <StarIcon className="cp_price-picker-option-indicator-icon" />
            )}
          </StyledElement>
        </div>
      </StyledElement>
    );
  };
  const PWYW_BLOCK_INPUT_CLASS = 'text-left';

  const renderPwywButton = price => {
    const ctx = computeOptionContext(price);
    const { priceId, isSelected } = ctx;
    return (
      <StyledElement
        as="div"
        key={priceId}
        elementPath="product.pricePicker.option"
        className={clsx(
          'cp_price-picker-option cp_price-picker-option--pwyw-button flex flex-col w-full text-left overflow-hidden',
          'border-solid [transition:border-color_0.15s_ease]',
          '[border-width:var(--cp_price-picker-option-border-width)]',
          '[border-radius:var(--cp_price-picker-option-border-radius)]',
          '[color:var(--cp_price-picker-option-color)]',
          {
            'cp_price-picker-option--selected': isSelected,
            'bg-[var(--cp_price-picker-option-background-color)] border-[var(--cp_price-picker-option-border-color)]':
              !isSelected,
            'bg-[var(--cp_price-picker-option-background-color-selected)] border-[var(--cp_price-picker-option-border-color-selected)] [box-shadow:inset_0_0_0_1px_var(--cp_price-picker-option-border-color-selected)]':
              isSelected,
          },
        )}
      >
        <div
          role="radio"
          tabIndex={0}
          aria-checked={isSelected}
          className="cp_price-picker-option-button"
          onClick={() => {
            onSelect && onSelect(priceId);
            onPwywOptionActivate && onPwywOptionActivate(priceId);
          }}
          onKeyDown={e => {
            if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
              e.preventDefault();
              onSelect && onSelect(priceId);
              onPwywOptionActivate && onPwywOptionActivate(priceId);
            }
          }}
        >
          <div className="cp_price-picker-option-content">
            <div className="cp_price-picker-option-lines">
              <div className="cp_price-picker-option-header">
                <div className="cp_price-picker-option-text">
                  <StyledElement
                    as="div"
                    elementPath="product.pricePicker.option.label"
                    className={clsx(
                      'cp_price-picker-option-label break-words',
                      '[color:var(--cp_price-picker-option-label-color)]',
                      '[font-family:var(--cp_price-picker-option-label-font-family)]',
                      '[font-style:var(--cp_price-picker-option-label-font-style)]',
                      '[font-size:var(--cp_price-picker-option-label-font-size)]',
                      '[line-height:var(--cp_price-picker-option-label-line-height)]',
                      '[font-weight:var(--cp_price-picker-option-label-font-weight)]',
                    )}
                  >
                    {ctx.effectiveLabel || 'Custom'}
                  </StyledElement>
                </div>
                <PriceBadge price={price} className={badgeClass} />
              </div>
            </div>
          </div>
          <StyledElement
            as="span"
            elementPath="product.pricePicker.option.indicator"
            className={clsx(
              'cp_price-picker-option-indicator',
              `cp_price-picker-option-indicator--shape-${shape}`,
              { 'cp_price-picker-option-indicator--checked': isSelected },
            )}
            aria-hidden="true"
          >
            {isSelected && shape === 'tick' && (
              <CheckIcon className="cp_price-picker-option-indicator-icon" />
            )}
            {isSelected && shape === 'star' && (
              <StarIcon className="cp_price-picker-option-indicator-icon" />
            )}
          </StyledElement>
        </div>
      </StyledElement>
    );
  };

  const renderPwywReveal = price => {
    const ctx = computeOptionContext(price);
    return (
      <div
        key={`reveal-${ctx.priceId}`}
        className="cp_price-picker-pwyw-reveal cp_price-picker-pwyw-block flex flex-col [grid-column:1/-1]"
        onClick={() => onSelect && onSelect(ctx.priceId)}
        onFocus={() => onSelect && onSelect(ctx.priceId)}
      >
        <div className="cp_price-picker-option-pwyw">
          <PayWhatYouWantPriceField
            price={price}
            labels={labels}
            isSelected
            showCurrency
            suffixElementPath="product.pricePicker.option.pwywSuffix"
            onSelect={() => onSelect && onSelect(ctx.priceId)}
            hideLabel
            inputClassName={PWYW_BLOCK_INPUT_CLASS}
          />
        </div>
        {renderOptionMeta(price, ctx)}
      </div>
    );
  };

  const renderPwywStandalone = price => {
    const ctx = computeOptionContext(price);
    return (
      <div
        key={`standalone-${ctx.priceId}`}
        className="cp_price-picker-pwyw-standalone cp_price-picker-pwyw-block flex flex-col [grid-column:1/-1]"
        onClick={() => onSelect && onSelect(ctx.priceId)}
        onFocus={() => onSelect && onSelect(ctx.priceId)}
      >
        {(ctx.effectiveLabel || price.badge || isBadgeIcon(price.badgeIcon)) && (
          <div className="flex flex-row items-start justify-between gap-2 w-full min-w-0 [margin-bottom:var(--cp_pwyw-label-padding-bottom,var(--cp_checkout-form-fields-label-padding-bottom))]">
            {ctx.effectiveLabel && (
              <StyledElement
                as="div"
                elementPath="product.pricePicker.option.label"
                className={clsx(
                  'cp_price-picker-option-label break-words min-w-0',
                  '[color:var(--cp_price-picker-option-label-color)]',
                  '[font-family:var(--cp_price-picker-option-label-font-family)]',
                  '[font-style:var(--cp_price-picker-option-label-font-style)]',
                  '[font-size:var(--cp_price-picker-option-label-font-size)]',
                  '[line-height:var(--cp_price-picker-option-label-line-height)]',
                  '[font-weight:var(--cp_price-picker-option-label-font-weight)]',
                )}
              >
                {ctx.effectiveLabel}
              </StyledElement>
            )}
            <PriceBadge
              price={price}
              className="cp_price-picker-option-badge ml-auto shrink-0 max-w-full whitespace-normal break-words"
            />
          </div>
        )}
        <div className="cp_price-picker-option-pwyw">
          <PayWhatYouWantPriceField
            price={price}
            labels={labels}
            isSelected={ctx.isSelected}
            showCurrency
            suffixElementPath="product.pricePicker.option.pwywSuffix"
            onSelect={() => onSelect && onSelect(ctx.priceId)}
            hideLabel
            inputClassName={PWYW_BLOCK_INPUT_CLASS}
          />
        </div>
        {renderOptionMeta(price, ctx)}
      </div>
    );
  };

  const renderPrices = list => {
    const out = [];
    list.forEach(price => {
      const isPwyw = price.payWhatYouWant;
      if (isPwyw && pwywMode === 'standalone') {
        out.push(renderPwywStandalone(price));
        return;
      }
      if (isPwyw && pwywMode === 'button') {
        out.push(renderPwywButton(price));
        if (String(selectedPriceId) === String(price._id)) out.push(renderPwywReveal(price));
        return;
      }
      out.push(renderOption(price));
    });
    return out;
  };

  if (useTabs) {
    const pricesInActive = enabledPrices.filter(p => labelOf(p) === activeGroup);

    if (isVerticalTabs) {
      return (
        <div
          className={clsx(
            'cp_price-picker cp_price-picker--toggle cp_price-picker--tabs-vertical',
            className,
          )}
        >
          <StyledElement
            as="div"
            elementPath="product.pricePicker.tabs"
            className="cp_price-tabs-vertical"
            role="tablist"
            aria-label="Select a price"
          >
            {groups.map(group => {
              const isActiveTab = group === activeGroup;
              const tabLabel = displayLabel(group);
              return (
                <button
                  key={group}
                  type="button"
                  role="tab"
                  aria-selected={isActiveTab}
                  className={clsx('cp_price-toggle-tab', {
                    'cp_price-toggle-tab--active': isActiveTab,
                  })}
                  onClick={() => selectTab(group)}
                >
                  {tabLabel}
                </button>
              );
            })}
          </StyledElement>
          {pricesInActive.length > 0 && (
            <div
              className={optionListClass}
              style={optionListStyle}
              role="radiogroup"
              aria-label="Select a price"
            >
              {renderPrices(pricesInActive)}
            </div>
          )}
        </div>
      );
    }

    return (
      <div className={clsx('cp_price-picker cp_price-picker--toggle', className)}>
        <StyledElement
          as="div"
          elementPath="product.pricePicker.tabs"
          className="cp_price-toggle"
          role="tablist"
          aria-label="Select a price"
        >
          {groups.map(group => {
            const isActiveTab = group === activeGroup;
            const tabLabel = displayLabel(group);
            return (
              <button
                key={group}
                type="button"
                role="tab"
                aria-selected={isActiveTab}
                className={clsx('cp_price-toggle-tab', {
                  'cp_price-toggle-tab--active': isActiveTab,
                })}
                onClick={() => selectTab(group)}
              >
                {tabLabel}
              </button>
            );
          })}
        </StyledElement>
        {pricesInActive.length > 0 && (
          <div
            className={optionListClass}
            style={optionListStyle}
            role="radiogroup"
            aria-label="Select a price"
          >
            {renderPrices(pricesInActive)}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={clsx(optionListClass, className)}
      style={optionListStyle}
      role="radiogroup"
      aria-label="Select a price"
    >
      {renderPrices(enabledPrices)}
    </div>
  );
};

export default PricePicker;
